import { Typography } from '@material-ui/core';
import React from 'react';
import { Check } from 'lucide-react';
import { AmplifyMcpSection } from '../../components/AmplifyProductMockups/AmplifyMcpSection';
import { CampaignManagementMockup } from '../../components/AmplifyProductMockups/CampaignManagementMockup';
import { CrossCampaignReportMockup } from '../../components/AmplifyProductMockups/CrossCampaignReportMockup';
import { LinkGeneratorMockup } from '../../components/AmplifyProductMockups/LinkGeneratorMockup';
import { YieldOptimizerMockup } from '../../components/AmplifyProductMockups/YieldOptimizerMockup';
import { Headline } from '../../components/Headline';
import { Kicker } from '../../components/Kicker';
import { Reveal } from '../../components/motion';
import { NewBadge, NoCodeBadge, JSPoweredBadge } from '../../components/NewBadge';
import PageHead from '../../components/PageHead';
import { SEO } from '../../components/SEO';
import MainLayout from '../../layouts/main';
import styled from '../../styled';
import { COLORS } from '../../themes/color';

const TiersGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  max-width: 780px;
  margin: 0 auto 80px;
  padding: 0 24px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const TierCard = styled('div')`
  background-color: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 32px 28px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: 100%;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(24, 144, 255, 0.12);
    transform: translateY(-2px);
  }
`;

const TierBadgeRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const TierTitle = styled('h3')`
  font-size: 22px;
  font-weight: 700;
  color: #0e1c2b;
  margin: 0;
`;

const TierDescription = styled('p')`
  font-size: 15px;
  line-height: 1.6;
  color: #4f4f4f;
  margin: 0;
`;

const TierSetup = styled('div')`
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
`;

const TierList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const TierItem = styled('li')`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 14px;
  line-height: 1.5;
  color: #4f4f4f;

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    color: ${COLORS.PRIMARY};
  }
`;

const LifecycleSection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 80px;
  padding: 0 24px;
`;

const LifecycleGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-top: 48px;
  align-items: stretch;

  > * {
    height: 100%;
  }

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const LifecycleStep = styled('div')`
  background-color: #f8fafc;
  border-radius: 12px;
  padding: 24px;
  height: 100%;
  box-sizing: border-box;

  .step {
    font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
    font-size: 12px;
    font-weight: 600;
    color: ${COLORS.PRIMARY};
    margin-bottom: 10px;
  }

  h3 {
    font-size: 17px;
    font-weight: 700;
    color: #0e1c2b;
    margin: 0 0 8px;
  }

  p {
    font-size: 14px;
    line-height: 1.6;
    color: #4f4f4f;
    margin: 0;
  }
`;

const ViewSection = styled('section')<{ flip?: boolean }>`
  max-width: 1100px;
  margin: 0 auto 80px;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 64px;
  align-items: center;
  direction: ${(p) => (p.flip ? 'rtl' : 'ltr')};

  > * {
    direction: ltr;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 32px;
    direction: ltr;
  }
`;

const ViewContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
`;

const ViewBody = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #4f4f4f;
  margin: 0;
`;

const ReportSection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 80px;
  padding: 0 24px;
`;

const ReportHeader = styled('div')`
  text-align: center;
  max-width: 680px;
  margin: 0 auto 40px;
`;

export default () => {
  return (
    <MainLayout>
      <div>
        <SEO
          pathname="/product/amplify/"
          title="Affilimate Amplify: Campaign Infrastructure for Publishers"
          description="Sponsored content campaigns and agentic link swap campaigns. Draft, review, launch, and measure on one platform, without adding headcount."
        />
        <PageHead style={{ marginBottom: 0 }}>
          <Reveal>
            <Kicker>amplify by affilimate</Kicker>
            <Headline
              variant="h1"
              gutterBottom
              component="h1"
              style={{
                maxWidth: '800px',
                margin: '0 auto 24px',
                fontWeight: 900,
                display: 'block'
              }}
            >
              Campaign infrastructure that works the way your team does{' '}
              <NewBadge>New</NewBadge>
            </Headline>
            <Typography
              variant="body1"
              color="textSecondary"
              style={{ maxWidth: '620px', margin: '24px auto 48px' }}
            >
              Sell sponsorships, activate swap inventory, or run both in one
              deal. One place to scope, flight, and prove every campaign
              without adding headcount.
            </Typography>
          </Reveal>
        </PageHead>

        <TiersGrid>
          <Reveal>
            <TierCard>
              <TierBadgeRow>
                <NoCodeBadge />
              </TierBadgeRow>
              <TierTitle>Sponsored content campaigns</TierTitle>
              <TierSetup>No dev work required</TierSetup>
              <TierDescription>
                Package new content, inclusions, and placements into
                sponsorships brands fund, with one report for every campaign
                instead of stitching SubIDs together in spreadsheets.
              </TierDescription>
              <TierList>
                <TierItem>
                  <Check size={16} />
                  See in-flight delivery before make-goods are due
                </TierItem>
                <TierItem>
                  <Check size={16} />
                  Pitch with evidence from your own page and link history
                </TierItem>
                <TierItem>
                  <Check size={16} />
                  Offer campaigns on a CPA, CPC, or flat spend basis
                </TierItem>
              </TierList>
            </TierCard>
          </Reveal>
          <Reveal delay={120}>
            <TierCard>
              <TierBadgeRow>
                <JSPoweredBadge />
              </TierBadgeRow>
              <TierTitle>AI-powered link swap campaigns</TierTitle>
              <TierSetup>One-time on-page integration</TierSetup>
              <TierDescription>
                Dramatically expand your sellable inventory by swapping
                existing product links to higher-yield merchants across your
                whole portfolio, scoped by AI and approved by you.
              </TierDescription>
              <TierList>
                <TierItem>
                  <Check size={16} />
                  Turn pages you already publish into new revenue streams
                </TierItem>
                <TierItem>
                  <Check size={16} />
                  Automate swaps at product and category level, not page by
                  page
                </TierItem>
                <TierItem>
                  <Check size={16} />
                  Auto-revert on goal completion or cutoff date
                </TierItem>
              </TierList>
            </TierCard>
          </Reveal>
        </TiersGrid>

        <LifecycleSection>
          <Reveal>
            <div style={{ textAlign: 'center' }}>
              <Headline component="h2">
                Pitch → Flight → Protect → Renew
              </Headline>
              <Typography
                variant="body1"
                color="textSecondary"
                style={{ maxWidth: '640px', margin: '16px auto 0' }}
              >
                Different deal structures, same commercial infrastructure.
                Sponsored content, link swaps, or both in one campaign.
              </Typography>
            </div>
          </Reveal>
          <LifecycleGrid>
            <Reveal>
              <LifecycleStep>
                <div className="step">01 · Pitch</div>
                <h3>Know what's sellable</h3>
                <p>
                  Historical page and link performance tells you what to
                  sell, how to price it, and where swap lift could strengthen
                  the deal.
                </p>
              </LifecycleStep>
            </Reveal>
            <Reveal delay={100}>
              <LifecycleStep>
                <div className="step">02 · Flight</div>
                <h3>Run every deal structure</h3>
                <p>
                  Flat fee, CPA, sponsored placements, link swaps, or a hybrid
                  in one campaign. Different execution, same goal tracking and
                  reporting.
                </p>
              </LifecycleStep>
            </Reveal>
            <Reveal delay={200}>
              <LifecycleStep>
                <div className="step">03 · Protect</div>
                <h3>Catch issues in flight</h3>
                <p>
                  Track pacing against goal while the campaign is still
                  running. See underdelivery before the make-good conversation
                  starts.
                </p>
              </LifecycleStep>
            </Reveal>
            <Reveal delay={300}>
              <LifecycleStep>
                <div className="step">04 · Renew</div>
                <h3>Prove it and sell the next one</h3>
                <p>
                  One record from first tracked link to client wrap. Walk into
                  the renewal with numbers the brand already trusts.
                </p>
              </LifecycleStep>
            </Reveal>
          </LifecycleGrid>
        </LifecycleSection>

        <ViewSection>
          <Reveal>
            <ViewContent>
              <Kicker style={{ marginBottom: 0 }}>Yield Optimizer</Kicker>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Find the money you're leaving on the table
              </Headline>
              <ViewBody>
                Many of your existing product links could earn more with a
                better merchant match. Finding them by hand does not scale.
                Yield Optimizer ranks the highest-upside swaps in your content
                and delivers each as a campaign draft with estimated lift,
                ready for your team to review and launch.
              </ViewBody>
              <ViewBody>
                Match existing product links against 100s of merchant feeds
                and millions of products, then act on the spread in minutes
                instead of quarters.
              </ViewBody>
            </ViewContent>
          </Reveal>
          <Reveal delay={150}>
            <YieldOptimizerMockup />
          </Reveal>
        </ViewSection>

        <ViewSection flip>
          <Reveal>
            <ViewContent>
              <Kicker style={{ marginBottom: 0 }}>Campaign management</Kicker>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Built for lean commerce teams
              </Headline>
              <ViewBody>
                Our customers run commerce businesses without an army of
                analysts. Amplify replaces the cross-team work of digging
                through analytics, identifying links in content, comparing
                advertisers, and proposing campaigns with one streamlined
                workflow your existing team can own.
              </ViewBody>
              <ViewBody>
                See which links, content, and products drive campaign
                performance. Understand what&apos;s working and where to
                intervene mid-flight.
              </ViewBody>
            </ViewContent>
          </Reveal>
          <Reveal delay={150}>
            <CampaignManagementMockup />
          </Reveal>
        </ViewSection>

        <ViewSection>
          <Reveal>
            <ViewContent>
              <Kicker style={{ marginBottom: 0 }}>Easy tracking</Kicker>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Every link connected to its campaign
              </Headline>
              <ViewBody>
                Generate campaign links in seconds. Each one is automatically
                connected to the right campaign, so every click, order, and
                dollar lands in your campaign report without manual tagging
                or cleanup.
              </ViewBody>
            </ViewContent>
          </Reveal>
          <Reveal delay={150}>
            <LinkGeneratorMockup />
          </Reveal>
        </ViewSection>

        <ReportSection>
          <Reveal>
            <ReportHeader>
              <Kicker style={{ marginBottom: 12 }}>
                Cross-campaign reporting
              </Kicker>
              <Headline component="h2" style={{ margin: '0 0 16px' }}>
                Know where every campaign stands
              </Headline>
              <ViewBody>
                Spend, delivery, and earnings for every campaign in one
                report: no exports to stitch, no SubIDs to piece together,
                no spreadsheets to maintain. Catch underdelivery while there
                is still time to fix it, and walk into renewals with numbers
                your clients can trust.
              </ViewBody>
            </ReportHeader>
          </Reveal>
          <Reveal delay={150}>
            <CrossCampaignReportMockup />
          </Reveal>
        </ReportSection>

        <AmplifyMcpSection />
      </div>
    </MainLayout>
  );
};
